import { useState, useEffect, useId } from 'react';
import { Collapsible, collapsibleChevron, Divider, Text } from '@/components/primitives';
import { SettingsDialog } from '@/components/dialogs';
import { AvatarEditor, type AvatarEditorProps, type CropData } from '@/components/forms/AvatarEditor';
import { InlineTextField } from '@/components/forms/InlineTextField';
import { useFeedbackZone } from '@/components/ui/FeedbackZone';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { signIn, getCurrentUser } from '@/auth/authData';
import { dialogMessages } from '@/constants';
import {
  updateContextLogo,
  removeContextLogo,
  updateContextName,
  updateContextHeaderLogo,
  removeContextHeaderLogo,
  updateContextBadgeLogo,
  removeContextBadgeLogo,
} from '@/api/profile';
import { uploadFileToS3, getMe } from '@/api';
import { FileAttachment, useFileRefRefetch, useUnconfirmedSave, extractFileDecline } from '@/features/files';
import type { FileRef } from '@/types/file';
import { Icons, vars, borders } from '@/theme';
import { testIds } from '@/test/testIds';
import { useOrganizations, useNetworks } from '@/features/context/hooks/useContextSwitching';
import { ContextSwitcherList } from '../ContextSwitcherList';
import { NoviSsoEditor } from './NoviSsoEditor';

export interface ContextSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

/** A flex-column link in the chain from the dialog body down to the switcher list (#1685-c). */
const FIT_COLUMN = { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 } as const;

/**
 * ContextSettingsDialog — the user-menu "Organization"/"Network" entry.
 *
 * Shows where the user currently is (logo + name + identifier) and — only when
 * the user can switch (`canSwitchContext`) — a switcher to move between the
 * orgs/networks they belong to. Logo AND name are editable for org admins /
 * network editors (the membership's canEditLogo, or staff); the identifier
 * (slug) is not. Name persists via PATCH /api/context/.
 *
 * Org/network *settings* (access flags, timezone, reminder schedule, …)
 * deliberately do NOT live here: they're staff config, owned by the staff
 * `/manage` area.
 */
export function ContextSettingsDialog({ open, onClose }: ContextSettingsDialogProps) {
  const { currentContext, canSwitchContext, isStaff, user } = useCurrentUser();
  const token = user?.token;

  // The switcher is tucked behind a disclosure: expand "Switch Organization" /
  // "Switch Network" / "Switch Organization/Network", then pick. The trigger
  // label reflects which context TYPES the user can actually switch to —
  // shares the query cache with ContextSwitcherList's own unfiltered fetch
  // (same query key), so this costs no extra request.
  const [switchOpen, setSwitchOpen] = useState(false);
  const { data: organizations = [] } = useOrganizations({ enabled: canSwitchContext });
  const { data: networks = [] } = useNetworks({ enabled: canSwitchContext });
  const hasOrgs = organizations.length > 0;
  const hasNetworks = networks.length > 0;
  // Staff can always reach both context types regardless of explicit
  // membership, so the label doesn't depend on the fetched lists for them.
  const switchLabel = isStaff || (hasOrgs && hasNetworks)
    ? 'Switch Organization/Network'
    : hasNetworks
      ? 'Switch Network'
      : 'Switch Organization';

  const isOrg = currentContext?.type === 'org';
  const org = currentContext?.organization;
  const network = currentContext?.network;
  const name = isOrg ? org?.name || org?.identifier : network?.name || network?.identifier;
  const identifier = isOrg ? org?.identifier : network?.identifier;
  // Logo + edit permission both come from the active context (org OR
  // network), surfaced per-context by /me. The FileRef is null when no logo is
  // set (→ placeholder) and carries processing/failed otherwise.
  // Editing is gated by the membership's canEditLogo and persisted via the
  // context-aware PUT /api/context/logo/.
  const logo = (isOrg ? org?.logo : network?.logo) ?? null;
  // Mirrors the backend rule (PUT /api/context/logo/): the membership's
  // canEditLogo grants edit, and staff may edit any context's logo regardless.
  const contextCanEditLogo = isOrg ? (org?.canEditLogo ?? false) : (network?.canEditLogo ?? false);
  const canEditLogo = contextCanEditLogo || isStaff;

  // Logo writes return only the new URL; refetch /me to refresh the stored
  // context (preserving the token, which /me does not return).
  const refreshUser = async () => {
    const fresh = await getMe(token);
    signIn({ ...fresh, token: token ?? '' });
  };

  // A re-read after a write is best-effort (#885 D5): a failed /me never
  // fails the write it follows, and never escapes as an unhandled rejection.
  const refreshUserBestEffort = () => {
    refreshUser().catch(() => {
      /* the next poll, focus refetch or reload re-reads /me */
    });
  };

  // Seed the stored context from a logo write's OWN response (#885 D5, the
  // way ProfileEditDialog's removeAvatar does): the new FileRef (processing)
  // is on screen at once, and useFileRefRefetch below polls from it. Skipped
  // when the response does not carry the field.
  const seedContextAsset = (field: 'logo' | 'headerLogo' | 'badgeLogo', ref: FileRef | null | undefined) => {
    if (ref === undefined) return;
    const current = getCurrentUser();
    const ctx = current?.context;
    if (!current || !ctx) return;
    if (ctx.type === 'org' && field === 'logo' && ctx.organization) {
      signIn({ ...current, context: { ...ctx, organization: { ...ctx.organization, logo: ref } } });
    } else if (ctx.type === 'network' && ctx.network) {
      signIn({ ...current, context: { ...ctx, network: { ...ctx.network, [field]: ref } } });
    }
  };

  // Transition discovery (file-handling §14.3.7). A /me re-read right after a
  // logo save can return the PRE-processing FileRef (§7.1 row 8) — this keeps
  // re-reading /me on a backoff until the rendition lands, so the logo
  // arrives on its own instead of the editor sitting on a placeholder.
  // I4 (#885a-4): a re-read that hands back the logo a save REPLACED (a
  // lagging replica, e.g. the old failed logo) is not the new file's outcome,
  // so polling goes on until the new file is read (bounded, R2).
  const logoSave = useUnconfirmedSave();
  const headerLogoSave = useUnconfirmedSave();
  const badgeLogoSave = useUnconfirmedSave();
  // Each asks its own question every render (no short-circuit): asking is
  // what lets a confirming read clear its record.
  const staleLogos = [
    logoSave.unconfirmed(logo),
    headerLogoSave.unconfirmed(network?.headerLogo),
    badgeLogoSave.unconfirmed(network?.badgeLogo),
  ];
  useFileRefRefetch([logo, network?.headerLogo, network?.badgeLogo], refreshUserBestEffort, {
    unconfirmed: staleLogos.includes(true),
  });

  // Name editing — same gate as the logo (canEditLogo). Persists via
  // PATCH /api/context/ then refetches /me (ContextNameEditor). `identifier`
  // (the slug) stays read-only. Seed from the raw name (not the identifier
  // display fallback).
  const rawName = (isOrg ? org?.name : network?.name) ?? '';

  // Logo saves and removals (#885a-1): the upload or the PUT failing rejects
  // (the editor keeps the old logo; ContextLogoEditor reports it once). The
  // re-read after an accepted PUT is best-effort (D5): it never turns the
  // write into a failure.
  const handleLogoSave = async (file: File, crop: CropData) => {
    const replaced = logo;
    const fileKey = await uploadFileToS3(file);
    const { logo: saved } = await updateContextLogo(fileKey, crop);
    seedContextAsset('logo', saved);
    logoSave.markSaved(replaced, saved);
    void refreshUser().catch(() => {});
  };

  const handleLogoDelete = async () => {
    const { logo: saved } = await removeContextLogo();
    seedContextAsset('logo', saved);
    logoSave.clear();
    void refreshUser().catch(() => {});
  };

  // Header logo is a NETWORK-only branding asset (transparent, no crop, served
  // raw). Editable under the same rule as the banner logo (canEditLogo|staff).
  const headerLogo = network?.headerLogo ?? null;
  const showHeaderLogo = !isOrg && canEditLogo;

  const handleHeaderLogoSave = async (file: File) => {
    const replaced = headerLogo;
    const fileKey = await uploadFileToS3(file);
    const { headerLogo: saved } = await updateContextHeaderLogo(fileKey);
    seedContextAsset('headerLogo', saved);
    headerLogoSave.markSaved(replaced, saved);
    void refreshUser().catch(() => {});
  };

  const handleHeaderLogoDelete = async () => {
    const { headerLogo: saved } = await removeContextHeaderLogo();
    seedContextAsset('headerLogo', saved);
    headerLogoSave.clear();
    void refreshUser().catch(() => {});
  };

  // Badge logo is a NETWORK-only asset (small square stamp for content cards).
  // Same gate as header logo.
  const badgeLogo = network?.badgeLogo ?? null;
  const showBadgeLogo = !isOrg && canEditLogo;

  const handleBadgeLogoSave = async (file: File) => {
    const replaced = badgeLogo;
    const fileKey = await uploadFileToS3(file);
    const { badgeLogo: saved } = await updateContextBadgeLogo(fileKey);
    seedContextAsset('badgeLogo', saved);
    badgeLogoSave.markSaved(replaced, saved);
    void refreshUser().catch(() => {});
  };

  const handleBadgeLogoDelete = async () => {
    const { badgeLogo: saved } = await removeContextBadgeLogo();
    seedContextAsset('badgeLogo', saved);
    badgeLogoSave.clear();
    void refreshUser().catch(() => {});
  };

  if (!currentContext?.type) return null;

  const PlaceholderIcon = isOrg ? Icons.organization : Icons.network;

  return (
    <SettingsDialog
      open={open}
      onClose={onClose}
      title={isOrg ? 'Organization' : 'Network'}
      maxWidth="sm"
      fitBody
    >
      <div data-testid={testIds.contextSettings.dialog} style={FIT_COLUMN}>
        {/* Logo + name + identifier — "this is where you are now". Wrapped in
            one relative block so the identifier can sit in its bottom-right
            corner without disturbing the centered logo/name layout. */}
        <div style={{ position: 'relative', paddingBottom: identifier ? 20 : 0 }}>
          {/* Logo */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            {canEditLogo ? (
              <ContextLogoEditor
                shape="square"
                fileRef={logo}
                aspectRatio={{ min: 2, max: 6 }}
                onSave={handleLogoSave}
                onDelete={logo ? handleLogoDelete : undefined}
                // The banner logo always renders on white in context, so preview
                // it on white too.
                previewBackground={vars.color.fixed.white}
              />
            ) : logo ? (
              // Read-only viewers go through FileAttachment so a logo that is
              // still processing (or failed) says so, instead of collapsing to
              // the same placeholder as "no logo at all" (§7.1 row 8).
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 8,
                  borderRadius: borders.radius.sm,
                  backgroundColor: vars.color.fixed.white,
                  border: `1px solid ${vars.color.surface.border}`,
                  maxHeight: 96,
                  overflow: 'hidden',
                }}
              >
                <FileAttachment fileRef={logo} variant="image" alt={`${name ?? ''} logo`} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 96,
                  height: 96,
                  borderRadius: borders.radius.sm,
                  backgroundColor: vars.color.fixed.white,
                  border: `1px solid ${vars.color.surface.border}`,
                }}
              >
                <PlaceholderIcon style={{ fontSize: 40, color: vars.color.neutral.subtle }} />
              </div>
            )}
          </div>

          {/* Name — "this is where you are now". Editable under the same gate
              as the logo. Full name always shown — no truncation. */}
          {canEditLogo ? (
            <ContextNameEditor
              rawName={rawName}
              placeholder={identifier ?? 'Name'}
              // The rename was accepted: the /me re-read is best-effort (#885 D5).
              onSaved={async () => refreshUserBestEffort()}
            />
          ) : (
            <Text
              variant="pageHeader"
              data-testid={testIds.contextSettings.name}
              style={{
                fontWeight: 600,
                textAlign: 'center',
                marginBottom: 8,
                display: 'block',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            >
              {name}
            </Text>
          )}

          {/* Identifier (slug) — read-only, secondary to the name. Labeled
              plain text (not a pill) tucked into the corner. */}
          {identifier && (
            <div
              data-testid={testIds.contextSettings.identifier}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                display: 'flex',
                gap: 4,
                alignItems: 'baseline',
              }}
            >
              <Text variant="caption" color="muted" style={{ fontSize: 11 }}>
                Identifier:
              </Text>
              <Text variant="caption" color="muted" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                {identifier}
              </Text>
            </div>
          )}
        </div>

        {/* Header logo — network branding only, editors/staff only. Same
            editable-logo pattern as the banner above, but no crop: it's a
            pre-sized transparent asset the backend serves as-is. */}
        {showHeaderLogo && (
          <div data-testid={testIds.contextSettings.headerLogoSection}>
            <Divider style={{ marginTop: 16, marginBottom: 12 }} />
            <Text variant="smallLabel" color="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Header logo
            </Text>
            {/* Preview on the actual dark app-header backdrop, at the header's
                own proportions: the logo there is ~32px tall and up to 200px
                wide (~6.25:1). Constrain by a fixed width so the box stays a
                short, wide strip — not the tall square the default would give. */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 250, maxWidth: '100%' }}>
                <ContextLogoEditor
                  shape="square"
                  fileRef={headerLogo}
                  aspectRatio={6.25}
                  enableCrop={false}
                  accept=".png,.svg,image/png,image/svg+xml"
                  onSave={(file) => handleHeaderLogoSave(file)}
                  onDelete={headerLogo ? handleHeaderLogoDelete : undefined}
                  previewBackground={vars.color.neutral.base}
                />
              </div>
            </div>
            <Text variant="caption" color="muted" style={{ display: 'block', marginTop: 6 }}>
              Transparent PNG or SVG, shown in the top-left app header.
            </Text>
          </div>
        )}

        {/* Badge logo — network-only, editors/staff only. Small square stamp
            shown on content cards to identify network-granted content. Same
            gate as header logo. */}
        {showBadgeLogo && (
          <div data-testid={testIds.contextSettings.badgeLogoSection}>
            <Divider style={{ marginTop: 16, marginBottom: 12 }} />
            <Text variant="smallLabel" color="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Badge logo
            </Text>
            {/* Preview tile at the approved frames' size (30 px): grey and
                empty until a logo is uploaded, the logo itself after. */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 30 }}>
                <ContextLogoEditor
                  shape="square"
                  fileRef={badgeLogo}
                  aspectRatio={1}
                  enableCrop={false}
                  accept=".png,image/png"
                  onSave={(file) => handleBadgeLogoSave(file)}
                  onDelete={badgeLogo ? handleBadgeLogoDelete : undefined}
                  previewBackground={vars.color.surface.muted}
                  emptyLabel={false}
                />
              </div>
            </div>
            <Text variant="caption" color="muted" style={{ display: 'block', marginTop: 6 }}>
              Square PNG, shown on your content in member organizations.
            </Text>
          </div>
        )}

        {/* NOVI SSO — per-network member SSO, same network-admin gate as the
            logos. Self-contained (loads/saves via /api/context/novi-sso/). */}
        {showHeaderLogo && (
          <>
            <Divider style={{ marginTop: 16, marginBottom: 12 }} />
            <NoviSsoEditor />
          </>
        )}

        {/* Switcher — only when the user actually belongs to more than one, and
            collapsed behind a disclosure: expand "Switch Organization/Network"
            (or the type-specific variant), then pick. */}
        {canSwitchContext && (
          <div data-testid={testIds.contextSettings.switchSection} style={FIT_COLUMN}>
            <Divider style={{ marginTop: 16, marginBottom: 12 }} />
            <Collapsible
              open={switchOpen}
              onOpenChange={setSwitchOpen}
              style={FIT_COLUMN}
              contentStyle={FIT_COLUMN}
              trigger={
                <button
                  type="button"
                  data-testid={testIds.contextSettings.switchToggle}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    outline: 'none',
                    textAlign: 'left',
                  }}
                >
                  <Icons.expandMore
                    fontSize="small"
                    className={collapsibleChevron}
                    style={{ color: vars.color.neutral.base, flexShrink: 0 }}
                  />
                  <Text variant="smallLabel" color="secondary">
                    {switchLabel}
                  </Text>
                </button>
              }
            >
              <div style={{ ...FIT_COLUMN, marginTop: 8 }}>
                <ContextSwitcherList onSwitched={onClose} />
              </div>
            </Collapsible>
          </div>
        )}
      </div>
    </SettingsDialog>
  );
}

/**
 * The editable context name, rendered INSIDE the SettingsDialog so its own
 * `useFeedbackZone()` resolves to the dialog's band (#871) —
 * ContextSettingsDialog's own zone is the PARENT layer (the StyledDialog's
 * layer provider is in SettingsDialog's render output, #821).
 *
 * The rename is a plain API call (PATCH /api/context/), not a mutation, so
 * nothing routes a 5xx or transport failure centrally: every failure is
 * reported here, once, and the field reverts to the saved name.
 */
function ContextNameEditor({
  rawName,
  placeholder,
  onSaved,
}: {
  rawName: string;
  placeholder: string;
  onSaved: () => Promise<void>;
}) {
  const zone = useFeedbackZone();
  const renameSource = useId();
  const [nameValue, setNameValue] = useState(rawName);
  useEffect(() => {
    setNameValue(rawName);
  }, [rawName]);
  const handleNameSave = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === rawName) return;
    try {
      await updateContextName(trimmed);
      // The rename itself succeeded: an earlier failure of it goes. The /me
      // re-read below is best-effort and not part of the action (#1628-c3).
      zone.clearSource(renameSource);
      await onSaved();
    } catch {
      setNameValue(rawName);
      zone.push(dialogMessages.contextSettings.renameFailed, 'error', { source: renameSource });
    }
  };
  return (
    <div
      data-testid={testIds.contextSettings.name}
      style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}
    >
      <InlineTextField
        value={nameValue}
        onChange={setNameValue}
        onSave={() => void handleNameSave()}
        placeholder={placeholder}
        variant="h6"
        fontWeight={600}
      />
    </div>
  );
}

/**
 * An AvatarEditor rendered INSIDE the SettingsDialog, so its own
 * `useFeedbackZone()` resolves to the dialog's band — ContextSettingsDialog's
 * own zone is the PARENT layer (the StyledDialog's layer provider is in
 * SettingsDialog's render output, #821).
 *
 * A failed removal is said here, once (#845): AvatarEditor swallows the
 * rejection and closes its confirm, so the caller owns the message. The logo
 * removals are plain API calls, not mutations, so nothing routes a 5xx or
 * transport failure centrally (no routeMutationError): every failure is
 * reported here. The rejection is re-thrown so AvatarEditor keeps the logo.
 *
 * A failed SAVE likewise (#885 D1): AvatarEditor says only a file decline
 * (422 `{code, message}`), inline under the logo; every other failure (the
 * upload, the PUT, a 5xx, the network) is said here, once, in the band, and
 * a decline never also here. The rejection is re-thrown so AvatarEditor
 * returns to the old logo.
 */
function ContextLogoEditor({ shape, onSave, onDelete, ...props }: AvatarEditorProps) {
  const zone = useFeedbackZone();
  // Each editor instance has its own save and remove keys (#1628-c3); the
  // save is one action, upload and PUT together.
  const saveSource = useId();
  const removeSource = useId();
  const handleSave: AvatarEditorProps['onSave'] = async (file, crop) => {
    try {
      await onSave(file, crop);
      zone.clearSource(saveSource);
    } catch (error) {
      if (!extractFileDecline(error)) {
        zone.push(dialogMessages.contextSettings.logoSaveFailed, 'error', { source: saveSource });
      }
      throw error;
    }
  };
  const handleDelete = onDelete
    ? async () => {
        try {
          await onDelete();
          zone.clearSource(removeSource);
        } catch (error) {
          zone.push(dialogMessages.contextSettings.logoRemoveFailed, 'error', { source: removeSource });
          throw error;
        }
      }
    : undefined;
  return <AvatarEditor shape={shape} {...props} onSave={handleSave} onDelete={handleDelete} />;
}
